import React, { memo, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";

import { ListPageStyled } from "Components/common/styles";
import { OTA_FRONTEND_VERSION } from "Utils/constants";

import { SetupView } from "./components/SetupView";
import { DeploymentsView } from "./components/DeploymentsView";
import { StatsView } from "./components/StatsView";
import { LogsView } from "./components/LogsView";
import { BulkVersionModal } from "./components/BulkVersionModal/BulkVersionModal";
import { SyncErrorsModal, SyncError } from "./components/SyncErrorsModal";
import {
  otaVehiclesApi,
  otaFleetsApi,
  otaComponentsApi,
  otaSystemApi,
} from "./api";
import {
  OTAManagementContainer,
  OTAManagementHeader,
  OTAManagementTitle,
  VersionInfo,
  ViewContainer,
} from "./styles";

const HeaderButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const BulkVersionButton = styled.button`
  padding: 8px 18px;
  border: 1px solid #0369a1;
  border-radius: 4px;
  background: #fff;
  color: #0369a1;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #0369a1;
    color: #fff;
  }
`;

const SyncButton = styled.button<{ $syncing?: boolean }>`
  padding: 8px 18px;
  border: 1px solid #2196f3;
  border-radius: 4px;
  background: ${({ $syncing }) => ($syncing ? "#e3f2fd" : "#fff")};
  color: #2196f3;
  font-size: 13px;
  font-weight: 600;
  cursor: ${({ $syncing }) => ($syncing ? "not-allowed" : "pointer")};
  transition: background 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    background: #2196f3;
    color: #fff;
  }
`;

export const OTAManagementPage = memo(() => {
  const location = useLocation();
  const viewParam = new URLSearchParams(location.search).get("view");
  const [isSyncing, setIsSyncing] = useState(false);
  const [isBulkVersionModalOpen, setIsBulkVersionModalOpen] = useState(false);
  const [backendVersion, setBackendVersion] = useState<string | null>(null);
  const [syncErrors, setSyncErrors] = useState<SyncError[]>([]);

  useEffect(() => {
    otaSystemApi
      .getBackendVersion()
      .then(setBackendVersion)
      .catch(() => undefined);
  }, []);

  const handleSyncGreengrass = useCallback(async () => {
    setIsSyncing(true);
    try {
      const [fleetResult, vehicleResult, componentResult] = await Promise.all([
        otaFleetsApi.syncGreengrass(),
        otaVehiclesApi.syncGreengrass(),
        otaComponentsApi.syncGreengrass(),
      ]);

      const linkedTotal =
        (fleetResult.linkedToLegacy ?? 0) + (vehicleResult.linkedToLegacy ?? 0);
      const provisionedTotal = fleetResult.provisionedToGg ?? 0;
      const enrichedTotal = vehicleResult.enriched ?? 0;
      const parts: string[] = [];
      if (componentResult.pulledToDb.length > 0)
        parts.push(
          `imported ${componentResult.pulledToDb.length} new version(s) from GG`
        );
      if (componentResult.deletedTotal > 0)
        parts.push(
          `removed ${componentResult.deletedTotal} orphaned version(s)`
        );
      if (linkedTotal > 0) parts.push(`linked ${linkedTotal} record(s) to FMS`);
      if (provisionedTotal > 0)
        parts.push(`provisioned ${provisionedTotal} fleet(s) into Greengrass`);
      if (enrichedTotal > 0)
        parts.push(`enriched ${enrichedTotal} vehicle(s) with fleet/type/VIN`);

      toast.success(
        parts.length > 0
          ? `Sync complete — ${parts.join(", ")}.`
          : "Sync complete."
      );

      const allErrors: SyncError[] = [
        ...(fleetResult.errors ?? []).map((msg) => ({
          source: "Fleet" as const,
          label: "",
          message: msg,
        })),
        ...(vehicleResult.errors ?? []).map((msg) => ({
          source: "Vehicle" as const,
          label: "",
          message: msg,
        })),
        ...(componentResult.errors ?? []).map((e) => ({
          source: "Component" as const,
          label: e.component + (e.version ? ` v${e.version}` : ""),
          message: e.error,
        })),
      ];
      if (allErrors.length > 0) {
        setSyncErrors(allErrors);
      }
    } catch {
      toast.error("Failed to trigger sync.");
    } finally {
      setIsSyncing(false);
    }
  }, []);

  const renderActiveView = () => {
    switch (viewParam) {
      case "deployments":
        return <DeploymentsView />;
      case "stats":
        return <StatsView />;
      case "logs":
        return <LogsView />;
      default:
        return <SetupView />;
    }
  };

  return (
    <ListPageStyled>
      <OTAManagementContainer>
        <OTAManagementHeader>
          <OTAManagementTitle>OTA Management System</OTAManagementTitle>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "4px",
            }}
          >
            <HeaderButtons>
              {!viewParam && (
                <BulkVersionButton
                  onClick={() => setIsBulkVersionModalOpen(true)}
                >
                  Bulk Create Versions
                </BulkVersionButton>
              )}
              <SyncButton
                $syncing={isSyncing}
                onClick={handleSyncGreengrass}
                disabled={isSyncing}
              >
                {isSyncing ? "Syncing…" : "Sync"}
              </SyncButton>
            </HeaderButtons>
            <VersionInfo>
              UI v{OTA_FRONTEND_VERSION}
              {backendVersion && <> &nbsp;·&nbsp; API v{backendVersion}</>}
            </VersionInfo>
          </div>
        </OTAManagementHeader>

        <ViewContainer>{renderActiveView()}</ViewContainer>
      </OTAManagementContainer>

      {isBulkVersionModalOpen && (
        <BulkVersionModal onClose={() => setIsBulkVersionModalOpen(false)} />
      )}

      {syncErrors.length > 0 && (
        <SyncErrorsModal
          errors={syncErrors}
          onClose={() => setSyncErrors([])}
        />
      )}
    </ListPageStyled>
  );
});
